import {
  Button,
  DrawerClose,
  DrawerContent,
  DrawerTitle,
  Separator,
  showErrorToast,
  showSuccessToast,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@bpinternal/ui-kit-next'
import { useQueryClient } from '@tanstack/react-query'
import { Content } from '@tiptap/core'
import { ChevronRight, Download, ExternalLink, Trash, X } from 'lucide-react'
import { DateTime } from 'luxon'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { PageTitleInput } from '~/components/PageTitleInput'
import { MinimalTiptapEditor } from '~/components/Tiptap'
import { Badge } from '~/elements'
import {
  useGetKnowledgeSourceFile,
  useRenameKnowledgeSource,
} from '~/features/knowledge-bases/hooks/useKnowledgeSource'
import { useWorkspaceMembers } from '~/hooks'
import { useFileContent } from '~/hooks/files/useFileContent'
import { useUploadFile } from '~/hooks/useUploadFile'
import { getQueryKey } from '~/services'
import { getApiClient } from '~/stores'
import { cn, formatters } from '~/utils'
import type { KnowledgeSource, KnowledgeSourceType } from '../../types'
import { getKnowledgeSourceOrigin, getKnowledgeSourceStatus, KnowledgeSourceIcon } from './KnowledgeSourceCell'

type KnowledgeSourceDrawerProps = {
  workspaceId: string
  botId: string
  kbId: string
  knowledgeSourceId: string
  knowledgeBaseName: string
  handleDelete: () => void
}

export const KnowledgeSourceDrawer = ({
  workspaceId,
  botId,
  kbId,
  knowledgeSourceId,
  knowledgeBaseName,
  handleDelete,
}: KnowledgeSourceDrawerProps) => {
  const {
    data: { file: knowledgeSource },
  } = useGetKnowledgeSourceFile({ workspaceId, botId, id: knowledgeSourceId })

  const tags = knowledgeSource.tags
  const dsType = tags['dsType'] as KnowledgeSourceType
  const faviconUrl = knowledgeSource.metadata?.['favicon']
  const knowledgeSourceName = useMemo(() => tags['title'] || knowledgeSource.key, [tags, knowledgeSource.key])

  const [name, setName] = useState(knowledgeSourceName)
  const { mutate: renameKnowledgeSource, isPending, isSuccess } = useRenameKnowledgeSource()

  return (
    <DrawerContent className="h-screen rounded-none text-foreground shadow-[-4px_4px_24px_8px_rgba(0,0,0,0.05)] md:w-[50vw] md:rounded-l-xl">
      <DrawerTitle className="sr-only">{name}</DrawerTitle>
      <div className="flex h-full w-full flex-col">
        <div className="flex-shrink-0 bg-background px-5 py-4 md:px-6">
          <div className="flex w-full items-center justify-end gap-4 md:justify-between ">
            <div className="hidden max-w-[90%] items-center gap-2 text-sm font-normal text-muted-foreground md:flex">
              <p className="truncate">{knowledgeBaseName}</p>
              <ChevronRight className="h-4 w-4 flex-shrink-0" />
              <p className="truncate">{name}</p>
            </div>
            <DrawerClose className="text-foreground">
              <X className="h-6 w-6 md:h-4 md:w-4" />
              <span className="sr-only">Close</span>
            </DrawerClose>
          </div>
          <div className="mt-4 flex w-full items-start justify-between gap-2 md:mt-10 md:gap-12">
            <div className="flex min-w-0 flex-1 items-start gap-3">
              {faviconUrl ? (
                <img src={faviconUrl} alt="Favicon" className="mt-1.5 h-6 w-6" />
              ) : (
                <KnowledgeSourceIcon contentType={knowledgeSource.contentType} dsType={dsType} size="6" />
              )}
              <DrawerTitle
                asChild
                data-vaul-no-drag
                className="h-16 min-w-0 select-text items-start p-0.5 text-2xl font-bold"
              >
                <PageTitleInput
                  defaultName={knowledgeSourceName}
                  onRename={(newName: string) => {
                    renameKnowledgeSource(
                      {
                        workspaceId,
                        botId,
                        id: knowledgeSource.id,
                        kbId,
                        tags: knowledgeSource.tags,
                        name: newName,
                      },
                      {
                        onSuccess: () => {
                          setName(newName)
                        },
                      }
                    )
                  }}
                  isPending={isPending}
                  isSuccess={isSuccess}
                  maxLength={64}
                  inputClassName="h-full"
                  showError
                />
              </DrawerTitle>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" size="icon" onClick={handleDelete} className="md:size-10">
                <Trash className="text-red-10" />
              </Button>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-5 pb-4 md:px-6" style={{ scrollbarGutter: 'stable' }}>
          <h4 className="text-xl font-semibold text-foreground">Details</h4>
          <Details knowledgeSource={knowledgeSource} workspaceId={workspaceId} dsType={dsType} />
          <Separator />
          <Preview knowledgeSource={knowledgeSource} dsType={dsType} workspaceId={workspaceId} botId={botId} />
        </div>
      </div>
    </DrawerContent>
  )
}

const Details = ({
  knowledgeSource,
  workspaceId,
  dsType,
}: {
  knowledgeSource: KnowledgeSource
  workspaceId: string
  dsType: KnowledgeSourceType
}) => {
  const { data: members } = useWorkspaceMembers({ workspaceId })

  let uploadedBy = 'Unknown'
  switch (knowledgeSource.owner.type) {
    case 'user': {
      const member = members?.find((member) => member.userId === knowledgeSource.owner.id)
      uploadedBy = member?.displayName ?? member?.email ?? 'User'
      break
    }
    case 'bot':
      uploadedBy = 'Bot'
      break
    default:
      uploadedBy = knowledgeSource.owner.name ?? 'Unknown'
  }

  return (
    <div className="mb-6 mt-4 grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3 md:grid-cols-4">
      <DetailItem label="Upload Date" value={DateTime.fromISO(knowledgeSource.createdAt).toRelative() ?? 'Unknown'} />
      <DetailItem label="Last Updated" value={DateTime.fromISO(knowledgeSource.updatedAt).toRelative() ?? 'Unknown'} />
      <DetailItem label="Uploaded by" value={uploadedBy} />
      <DetailItem
        label="Origin"
        value={
          <div className="flex w-fit items-center rounded-full bg-secondary px-2 py-1 text-xs text-foreground">
            {getKnowledgeSourceOrigin(dsType)}
          </div>
        }
      />
      <DetailItem label="Status" value={getKnowledgeSourceStatus(knowledgeSource.status).text} />
      <DetailItem label="File size" value={formatters.byte(knowledgeSource.size ?? 0)} />
      <DetailItem
        label="Tags"
        className="col-span-full"
        value={
          <div className="flex flex-wrap gap-1">
            {Object.entries(knowledgeSource.tags).map(([key, value]) => (
              <div key={key} className="inline-flex max-w-full items-center rounded-md bg-secondary px-2 py-1 text-xs">
                <span className="mr-1.5 flex-shrink-0 font-medium text-foreground">{key}:</span>
                <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-muted-foreground">
                  {value}
                </span>
              </div>
            ))}
          </div>
        }
      />
    </div>
  )
}

const DetailItem = ({ label, value, className }: { label: string; value: string | ReactNode; className?: string }) => {
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <p className="text-sm text-muted-foreground">{label}</p>
      <div className="text-sm text-foreground">{value}</div>
    </div>
  )
}

const Preview = ({
  knowledgeSource,
  dsType,
  workspaceId,
  botId,
}: {
  knowledgeSource: KnowledgeSource
  dsType: KnowledgeSourceType
  workspaceId: string
  botId: string
}) => {
  const showOriginal = dsType !== 'web-page'
  const isRichText = dsType === 'rich-text'
  const showIndexed = !isRichText && knowledgeSource.index
  const [richTextContent, setRichTextContent] = useState<string>('')
  const [currentUrl, setCurrentUrl] = useState<string>(knowledgeSource.url)
  const activeTab = showOriginal ? 'Original' : 'Indexed'

  useEffect(() => {
    setCurrentUrl(knowledgeSource.url)
  }, [knowledgeSource.url])

  return (
    <div className="mt-6 flex flex-1 flex-col gap-4 text-foreground md:mt-10">
      <Tabs defaultValue={activeTab} className="flex flex-1 flex-col">
        <PreviewHeader
          showOriginal={showOriginal}
          showIndexed={showIndexed}
          isRichText={isRichText}
          knowledgeSource={{ ...knowledgeSource, url: currentUrl }}
          richTextContent={richTextContent}
          workspaceId={workspaceId}
          botId={botId}
          onUrlUpdate={setCurrentUrl}
        />
        {showOriginal && (
          <TabsContent value="Original" className="mt-4 h-full">
            {isRichText ? (
              <RichTextPreview knowledgeSource={knowledgeSource} onContentChange={setRichTextContent} />
            ) : (
              <div className="select-text rounded-lg bg-muted p-6" data-vaul-no-drag>
                <FileContentDisplay url={knowledgeSource.url} />
              </div>
            )}
          </TabsContent>
        )}
        <TabsContent value="Indexed" className="mt-4">
          <div className="select-text overflow-x-auto rounded-lg bg-muted p-6" data-vaul-no-drag>
            <IndexedContentDisplay knowledgeSource={knowledgeSource} workspaceId={workspaceId} botId={botId} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}

const PreviewHeader = ({
  showOriginal,
  showIndexed,
  isRichText,
  knowledgeSource,
  richTextContent,
  workspaceId,
  botId,
  onUrlUpdate,
}: {
  showOriginal: boolean
  showIndexed: boolean
  isRichText: boolean
  knowledgeSource: KnowledgeSource
  richTextContent: string
  workspaceId: string
  botId: string
  onUrlUpdate: (url: string) => void
}) => {
  const actionButtons = (
    <div className="flex gap-2">
      <DownloadButton knowledgeSource={knowledgeSource} />
      {isRichText && (
        <SaveButton
          content={richTextContent}
          knowledgeSource={knowledgeSource}
          workspaceId={workspaceId}
          botId={botId}
          onUrlUpdate={onUrlUpdate}
        />
      )}
    </div>
  )

  if (showOriginal && showIndexed) {
    return (
      <div className="flex w-full flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex w-full items-center gap-2 md:w-auto">
          <TabsList className="h-12 w-full md:h-auto md:w-auto">
            <TabsTrigger className="h-full w-1/2 md:w-auto" value="Original">
              Original
            </TabsTrigger>
            <TabsTrigger className="h-full w-1/2 md:w-auto" value="Indexed">
              Indexed
            </TabsTrigger>
          </TabsList>
          {showIndexed && (
            <div className="hidden md:block">
              <IndexedTooltip />
            </div>
          )}
        </div>
        <div className="flex w-full items-center justify-between md:w-auto">
          <div className="flex items-center gap-2">
            {showIndexed && (
              <div className="md:hidden">
                <IndexedTooltip />
              </div>
            )}
          </div>
          {actionButtons}
        </div>
      </div>
    )
  }

  return (
    <div className="flex w-full items-center justify-between">
      <div className="flex items-center gap-2">
        <Badge variant="secondary" className="h-8 w-fit px-3">
          {showOriginal ? 'Original' : 'Indexed'}
        </Badge>
        {showIndexed && <IndexedTooltip />}
      </div>
      {actionButtons}
    </div>
  )
}

const RichTextPreview = ({
  knowledgeSource,
  onContentChange,
}: {
  knowledgeSource: KnowledgeSource
  onContentChange: (content: string) => void
}) => {
  const { content: initialContent, loading, error } = useFileContent(knowledgeSource.url)
  const [content, setContent] = useState<string>('')
  const [editorReloadTrigger, setEditorReloadTrigger] = useState(false)

  useEffect(() => {
    if (initialContent) {
      setContent(initialContent)
      setEditorReloadTrigger((prev) => !prev)
    }
  }, [initialContent])

  const handleContentChange = (richText: Content) => {
    const newContent = String(richText)
    setContent(newContent)
    onContentChange(newContent)
  }

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading content...</p>
  }

  if (error) {
    return <p className="text-sm text-destructive">Error: {error}</p>
  }

  return (
    <div data-vaul-no-drag className="h-full space-y-2">
      <MinimalTiptapEditor
        key={editorReloadTrigger ? 'reload' : 'load'}
        content={content}
        onChange={handleContentChange}
        className="h-full w-full text-sm"
        editorContentClassName="p-3 sm:p-5"
        output="html"
        placeholder="Add FAQs, documentation, or any text content your bot should reference"
        autofocus={true}
        editable={!loading}
        editorClassName="focus:outline-hidden"
      />
    </div>
  )
}

const IndexedTooltip = () => {
  return (
    <>
      <div className="hidden md:block">
        <Tooltip>
          <TooltipContent className="max-w-[250px]" sideOffset={10} side="right">
            <p className="font-medium">What is Indexed?</p>
            <br />
            <p>
              Extracted and processed information from your file that the AI can search, reference, and use to answer
              questions
            </p>
          </TooltipContent>
          <TooltipTrigger>
            <Badge
              color="gray"
              variant="secondary"
              className="flex h-6 w-6 items-center justify-center rounded-full text-foreground"
            >
              ?
            </Badge>
          </TooltipTrigger>
        </Tooltip>
      </div>
      <a
        href="#" //TODO: Add link to documentation
        target="_blank"
        rel="noreferrer noopener"
        className="flex flex-row items-center gap-2 px-4 text-sm text-muted-foreground md:hidden"
      >
        <ExternalLink size={16} /> Learn more about Indexed
      </a>
    </>
  )
}

const SaveButton = ({
  className,
  content,
  knowledgeSource,
  workspaceId,
  botId,
  onUrlUpdate,
}: {
  className?: string
  content: string
  knowledgeSource: KnowledgeSource
  workspaceId: string
  botId: string
  onUrlUpdate?: (url: string) => void
}) => {
  const { mutateAsync: uploadFile, isPending } = useUploadFile({ botId, workspaceId })
  const queryClient = useQueryClient()
  const [saveDisabled, setSaveDisabled] = useState(false)

  useEffect(() => {
    setSaveDisabled(!content.trim())
  }, [content])

  const handleSave = async () => {
    if (!content.trim()) return
    setSaveDisabled(true)

    try {
      const result = await uploadFile({
        key: knowledgeSource.key,
        content,
        contentType: 'text/html',
        index: true,
        tags: knowledgeSource.tags,
        accessPolicies: knowledgeSource.accessPolicies,
      })

      if (result?.file?.url && onUrlUpdate) {
        onUrlUpdate(result.file.url)
      }

      await queryClient.invalidateQueries({
        queryKey: getQueryKey('workspaces_/$workspaceId_/bots_/$botId_/files/$id', {
          workspaceId,
          botId,
          id: knowledgeSource.id,
        }),
      })

      content = ''

      showSuccessToast('Rich text content saved successfully')
    } catch (error) {
      showErrorToast('Failed to save rich text content')
    }
  }

  return (
    <Button
      className={cn(className, 'w-20')}
      onClick={handleSave}
      disabled={isPending || !content.trim() || saveDisabled}
    >
      {isPending ? 'Saving...' : 'Save'}
    </Button>
  )
}

const DownloadButton = ({ className, knowledgeSource }: { className?: string; knowledgeSource: KnowledgeSource }) => {
  const handleDownload = async () => {
    try {
      const response = await fetch(knowledgeSource.url)
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = knowledgeSource.tags['title'] || knowledgeSource.key
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      // Handle download error silently or show user feedback
    }
  }

  return (
    <Button className={className} variant="ghost" size="icon" onClick={handleDownload}>
      <Download />
    </Button>
  )
}

const FileContentDisplay = ({ url }: { url: string }) => {
  const { content, loading, error, isIframe } = useFileContent(url)

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading content...</p>
  }

  if (error) {
    return <p className="text-sm text-destructive">Error: {error}</p>
  }

  if (isIframe) {
    const googleDocsViewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(url)}&embedded=true`
    return (
      <div className="h-[70vh] w-full">
        <iframe
          src={googleDocsViewerUrl}
          className="h-full w-full rounded border"
          title="Document Preview"
          loading="lazy"
        />
      </div>
    )
  }

  return <pre className="overflow-x-auto whitespace-pre-wrap text-sm">{content}</pre>
}

const beautifyText = (inputText: string) => {
  // Replace multiple newline characters with a unique placeholder
  let processedText = inputText.replace(/\n\s*\n/g, 'MULTIPLE_NEWLINES')

  // Replace single newline characters (joining words split into multiple lines)
  processedText = processedText.replace(/\n/g, ' ')

  // Replace the unique placeholder with two newline characters (preserving paragraphs)
  processedText = processedText.replace(/MULTIPLE_NEWLINES/g, '\n\n')

  // Further clean-up if necessary. For example, you may want to replace multiple spaces with a single one
  processedText = processedText.replace(/  +/g, ' ')

  return processedText
}

const IndexedContentDisplay = ({
  knowledgeSource,
  workspaceId,
  botId,
}: {
  knowledgeSource: KnowledgeSource
  workspaceId: string
  botId: string
}) => {
  const [chunks, setChunks] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchIndexedContent = async () => {
      try {
        setLoading(true)
        setError(null)

        if (knowledgeSource.index) {
          const client = getApiClient({ workspaceId, botId })
          const { passages } = await client.listFilePassages({ id: knowledgeSource.id, limit: 200 })
          setChunks(passages.map((p) => beautifyText(p.content)))
        } else {
          setChunks(['No indexed content available for this knowledge source.'])
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load indexed content')
      } finally {
        setLoading(false)
      }
    }

    fetchIndexedContent()
  }, [knowledgeSource.index, knowledgeSource.id, workspaceId, botId])

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-sm text-muted-foreground">Loading indexed content...</div>
      </div>
    )
  }

  if (error) {
    return <div className="text-sm text-destructive">Error: {error}</div>
  }

  return (
    <div className="space-y-6">
      {chunks.map((chunk, i) => (
        <div key={i}>
          {i > 0 && <div className="my-6 border-t border-border" />}
          <div className="prose prose-sm max-w-none">
            <pre className="whitespace-pre-wrap text-sm leading-relaxed">{chunk}</pre>
          </div>
        </div>
      ))}
    </div>
  )
}
